import { Box } from '@customink/pigment-react';
import type { ReactNode } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';

import { useStickyBarContext } from '@/contexts/StickyBarContext';
import { colorSmoke } from '@/utils/designTokens';

interface StickyButtonBarProps {
  children: ReactNode;
}

export function StickyButtonBar({ children }: StickyButtonBarProps) {
  const [isSticky, setIsSticky] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { isApplePayVisible } = useStickyBarContext();

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is visible, the bar should not be sticky
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            padding: isApplePayVisible ? '8px 0' : '12px 0',
            margin: isSticky ? '0 8px' : 0,
            transition: '150ms',
            // Add pseudo element for background when sticky
            '&::before': isSticky
              ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '50%',
                  right: 0,
                  transform: 'translateX(-50%)',
                  width: '100vw',
                  backgroundColor: 'white',
                  borderTop: `1px solid ${colorSmoke}`,
                  zIndex: -1,
                }
              : {},
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Sentinel element to detect when button should not be sticky */}
      <Box
        aria-hidden="true"
        ref={sentinelRef}
        sx={{
          height: '1px',
          width: '100%',
        }}
      />
    </>
  );
}

export default StickyButtonBar;
